import React, { useEffect, useRef, useState } from 'react';
import { CloudUploadOutlined } from '@ant-design/icons';
import Cropper, { Area } from 'react-easy-crop';
import '../styles/imageInput.css';
import { Button, ColorPicker, Flex, Modal, Radio, Slider, Spin } from 'antd';
import getCroppedImg from '../utils/getCroppedImage';
import { extractColors } from 'extract-colors';
import { isColorTooBright } from '../utils/colorCheck';
import { createObjectUrlFromRemoteImage } from '../utils/imageCompression';
import { IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { CropIcon, FitToScreenIcon } from '@hugeicons/core-free-icons';

const ImageUploadInput = ({
                            setImage,
                            imagePreviewUrl,
                            setImagePreviewUrl,
                            objectFit = 'contain',
                            setColor,
                            color,
                            colorPickerError,
                            setColorPickerError,
                            onRemoveImage
                          }: any) => {
  const fileInputRef = useRef(null);
  const paddedCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [imageLoading, setImageLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const [cropperOpen, setCropperOpen] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({ width: 0, height: 0, y: 0, x: 0 });
  const [colorSwatches, setColorSwatches] = useState<any>(undefined);
  const [cropMode, setCropMode] = useState<'crop' | 'fit'>('crop');
  const [paddedImagePreviewUrl, setPaddedImagePreviewUrl] = useState<string | null>(null);
  const [isGeneratingPaddedPreview, setIsGeneratingPaddedPreview] = useState(false);


  useEffect(() => {
    if (imagePreviewUrl && setColor) {
      createObjectUrlFromRemoteImage(imagePreviewUrl).then((url: any) => {
        extractColors(url, { colorValidator: (r, g, b) => !isColorTooBright(r, g, b) }).then((colors) => {
          setColorSwatches(colors);
        });
      });
    }
  }, [imagePreviewUrl]);

  useEffect(() => {
    if (cropMode === 'fit' && imageToCrop) {
      setIsGeneratingPaddedPreview(true);
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = imageToCrop;
      image.onload = () => {
        const targetAspect = 1.4;
        const imgAspect = image.width / image.height;

        let canvasWidth, canvasHeight;
        if (imgAspect > targetAspect) {
          canvasWidth = image.width;
          canvasHeight = Math.round(canvasWidth / targetAspect);
        } else {
          canvasHeight = image.height;
          canvasWidth = Math.round(canvasHeight * targetAspect);
        }

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d')!;

        // ✅ sfondo bianco per evitare il nero
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // sfondo blur
        ctx.filter = 'blur(20px)';
        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
        ctx.filter = 'none';

        // overlay bianco per schiarire
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        const offsetX = (canvasWidth - image.width) / 2;
        const offsetY = (canvasHeight - image.height) / 2;

        // sfondo bianco sotto l'immagine centrata (copre eventuali trasparenze)
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(offsetX, offsetY, image.width, image.height);

        // immagine originale centrata sopra lo sfondo
        ctx.drawImage(image, offsetX, offsetY);

        // salva anteprima
        const url = canvas.toDataURL('image/jpeg');
        setPaddedImagePreviewUrl(url);
        setIsGeneratingPaddedPreview(false);
      };
    }
  }, [cropMode, imageToCrop]);


  const handleChange = (e: any) => {
    if (e.target.files[0]) {
      setCropperOpen(true);
      setImageToCrop(URL.createObjectURL(e.target.files[0]));
      e.target.value = null;
    }
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files[0]) {
      setCropperOpen(true);
      setImageToCrop(URL.createObjectURL(event.dataTransfer.files[0]));
    }
  };

  const cropImage = async () => {
    try {
      const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
      setZoom(1);
      setImagePreviewUrl(croppedImage.url);
      setImage(croppedImage.file);
      setCropperOpen(false);
      const colors = await extractColors(croppedImage.url, { colorValidator: (r, g, b) => !isColorTooBright(r, g, b) });
      setColorSwatches(colors);
      setColor(colors[0].hex);
    } catch (e) {
      console.error(e);
    }
  };

  const fitImageWithPadding = async () => {
    return new Promise<void>((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = imageToCrop;
      image.onload = async () => {
        const targetAspect = 1.4;
        const imgAspect = image.width / image.height;

        let canvasWidth, canvasHeight;
        if (imgAspect > targetAspect) {
          canvasWidth = image.width;
          canvasHeight = Math.round(canvasWidth / targetAspect);
        } else {
          canvasHeight = image.height;
          canvasWidth = Math.round(canvasHeight * targetAspect);
        }

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d')!;

        // ✅ 1. sfondo bianco per il canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // ✅ 2. sfondo blur
        ctx.filter = 'blur(20px)';
        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
        ctx.filter = 'none';

        // ✅ 3. overlay bianco semitrasparente per schiarire
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // ✅ 4. immagine centrata con sfondo bianco sotto (copre trasparenze)
        const offsetX = (canvasWidth - image.width) / 2;
        const offsetY = (canvasHeight - image.height) / 2;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(offsetX, offsetY, image.width, image.height);
        ctx.drawImage(image, offsetX, offsetY);

        // ✅ 5. esporta
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], 'padded-image.jpg', { type: 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          setImage(file);
          setImagePreviewUrl(url);
          setCropperOpen(false);

          const colors = await extractColors(url, {
            colorValidator: (r, g, b) => !isColorTooBright(r, g, b)
          });
          setColorSwatches(colors);
          setColor(colors[0]?.hex);
          resolve();
        }, 'image/jpeg', 0.95);
      };
    });
  };



  function onColorPickerChange(color: any, hex: string) {
    const rgb = color.toRgb();
    if (isColorTooBright(rgb.r, rgb.g, rgb.b)) {
      setColorPickerError(true);
    } else {
      setColorPickerError(false);
      setColor('#' + color.toHex());
    }
  }

  return (
    <>
      <Modal
        title={<div />}
        open={cropperOpen}
        onCancel={() => setCropperOpen(false)}
        footer={
          <Button type={'primary'} className={'w-full mt-2'} onClick={async () => {
            if (cropMode === 'crop') await cropImage();
            else await fitImageWithPadding();
          }}>Usa Immagine</Button>
        }
      >
        <div style={{ position: 'relative', height: 300 }}>
          {cropMode === 'crop' ? (
            <Cropper
              onCropComplete={(croppedArea, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
              aspect={1.4}
              style={{ containerStyle: { borderRadius: 5 } }}
              image={imageToCrop}
              onCropChange={setCrop}
              crop={crop}
              zoom={zoom}
            />
          ) : isGeneratingPaddedPreview ? (
            <Flex align="center" justify="center" style={{ height: '100%' }}>
              <Spin />
            </Flex>
          ) : (
            <img
              src={paddedImagePreviewUrl || ''}
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 5 }}
              alt="Anteprima con bordi"
            />
          )}
        </div>
        <div className={'flex gap-2 items-center justify-center mt-2'}>
          <Radio.Group buttonStyle={'solid'} value={cropMode} onChange={(e) => setCropMode(e.target.value)}>
            <Radio.Button className={'hover:text-slate-400'} value="crop">
              <div className={'flex items-center gap-2'}><HugeiconsIcon size={16} icon={CropIcon} /> Ritaglia</div>
            </Radio.Button>
            <Radio.Button className={'hover:text-slate-400'} value="fit">
              <div className={'flex items-center gap-2'}><HugeiconsIcon size={16} icon={FitToScreenIcon} /> Adatta</div>
            </Radio.Button>
          </Radio.Group>
        </div>
        {cropMode === 'crop' && (
          <Flex align={'center'} gap={10}>
            <p>Zoom</p>
            <Slider value={zoom} style={{ flex: 1 }} tooltip={{ formatter: null }} onChange={(val) => {
              setZoom(val);
            }} step={0.1} min={1} max={2} />
          </Flex>
        )}
      </Modal>
      <div onDragOver={handleDragOver} onDrop={handleDrop} className={'inputWrapper bg-slate-50'}>
        {onRemoveImage && imagePreviewUrl && (
          <div style={{ position: 'absolute', zIndex: 2, top: 5, left: 5 }}>
            <Button onClick={onRemoveImage} style={{ width: 35, height: 35, padding: 0 }}><IconX /></Button>
          </div>
        )}
        <label htmlFor={'input'} style={{ cursor: 'pointer' }}>
          <div className={!imagePreviewUrl || imagePreviewUrl === 'error' ? 'uploadLabel' : 'uploadLabel transparent'}>
            <CloudUploadOutlined style={{ fontSize: 20 }} />
            <div style={{ textAlign: 'center' }}>
              <p>Carica immagine</p>
              <p style={{ fontSize: 12, marginTop: 5 }}>1000x1400</p>
            </div>
          </div>
          {imagePreviewUrl && !cropperOpen && (
            <img onLoad={() => setImageLoading(false)} src={imagePreviewUrl} width={'100%'}
                 style={{ objectFit: objectFit, display: 'block' }} />
          )}
        </label>
        <input ref={fileInputRef} accept={'.png,.jpeg,.jpg'} id={'input'} type={'file'} onChange={handleChange}
               style={{ display: 'none' }} />
      </div>
      <AnimatePresence>
        {colorSwatches && setColor && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 40, opacity: 1 }}>
            <Flex style={{ marginTop: 10 }} gap={5} align={'center'}>
              <Flex
                className={colorPickerError ? 'colorPicker error' : !colorSwatches.some((sw: any) => sw.hex === color) ? 'colorPicker selected' : 'colorPicker'}
                align={'center'} gap={5}>
                <ColorPicker
                  defaultFormat={'hex'}
                  format={'hex'}
                  value={color}
                  onChange={onColorPickerChange}
                  disabledAlpha
                  style={{
                    border: 'none',
                    padding: 0,
                    backgroundColor: 'transparent',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                />
                <p>{colorPickerError ? 'troppo chiaro' : color}</p>
              </Flex>
              <Flex gap={5}>
                {colorSwatches.map((colorSwatch: any, index: number) => (
                  <div onClick={() => {
                    setColor(colorSwatch.hex);
                    setColorPickerError(false);
                  }} className={color === colorSwatch.hex ? 'colorSwatch selected' : 'colorSwatch'} key={index}>
                    <div
                      style={{ backgroundColor: colorSwatch.hex, width: '100%', height: '100%', borderRadius: 20 }} />
                  </div>
                ))}
              </Flex>
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageUploadInput;
